import React from 'react';
import {useParams} from 'react-router';

import {useIsInLoadedReportContext} from '../../pages/ReportPage/LoadedReportContext';
import {useHighlightSource} from '../../state/interactState/highlightSource';
import {
  useSetHighlight,
  useSetHighlights,
} from '../../state/interactState/hooks';
import {
  hoveredIssueSource,
  useActiveIssues,
  useAnnotationActive,
  useHandleIssueInteraction,
  useIssueRampStatus,
  useSetSelectedIssue,
} from '../../state/issues/hooks';
import {useUPlotMode} from '../../util/rampFeatureFlags/linePlotDynamicConfigs';
import {useRampFlagUPlotUser} from '../../util/rampFeatureFlags/rampFeatureFlags';
import {useColorMode} from '../../util/useColorMode';
import {useSingleRunModeContext} from '../MultiRunWorkspace/SingleRunMode';
import {useIsFullScreenPanel} from '../Panel/hooks/useIsFullScreenPanel';
import {useStablePanelId} from '../Panel/PanelIDContext';
import {useWorkspaceSettingsColorPalette} from '../WorkspaceDrawer/Settings/colorPalette/ColorPalette';
import {getCascadingSetting} from '../WorkspaceDrawer/Settings/getCascadingSetting';
import {
  useMaybeHasMediaPanel,
  useMaybeLinePlotShowXRange,
  useMaybeSetWorkspaceSliderSteps,
} from '../WorkspaceDrawer/Settings/hooks';
import {useSectionSettingsContextMaybe} from '../WorkspaceDrawer/Settings/SectionSettingsContextProvider';
import {useMaybeWorkspaceDrawerContext} from '../WorkspaceDrawer/WorkspaceDrawerContextProvider';
import {type GraphProps, GraphWrapper} from './Graph';
import {usePanelConfigContext} from './PanelConfigContext';
import {usePanelInteractionContext} from './PanelInteractionContext';
import {usePanelTimeContext} from './PanelTimeContext';
import {usePanelZoom} from './PanelZoomContext';
import {usePanelGroupingSettings} from './RunsLinePlotContext/usePanelGroupingSettings';
import {useSetSharedPanelZoom} from './SharedPanelZoomContext';
import {usePanelSystemMetricsContext} from './SystemMetrics/PanelSystemMetricsContext';
import {usePanelZoom2} from './zoom/PanelZoomContext2';

type GraphContainerProps = Omit<
  GraphProps,
  | 'cappedPanel'
  | 'display'
  | 'highlight'
  | 'issueMarkers'
  | 'panelId'
  | 'plotSettings'
  | 'sizeRef'
  | 'zoom'
  | 'zoomUsage'
>;

/**
 * Reads the contexts Graph needs and passes their values as props, so Graph
 * can render without knowing where the panel is mounted: the panel ID, the
 * Delay Render opt-in, the display settings, zoom, zoom usage telemetry, the
 * layout's size ref, the issue markers, the cross-panel highlight, and the
 * settings the renderers, crosshair, legend, and warning read.
 */
export function GraphContainer(props: GraphContainerProps) {
  const panelId = useStablePanelId();
  const {isDelayedSystemMetrics, setAllowUncappedMetrics} =
    usePanelSystemMetricsContext();
  const cappedPanel: GraphProps['cappedPanel'] = {
    isDelayed: isDelayedSystemMetrics,
    setAllowUncappedMetrics,
  };

  const isInReport = useIsInLoadedReportContext();
  const {isFullScreen} = useIsFullScreenPanel();
  const zoomUsage: GraphProps['zoomUsage'] = {
    isFullScreen,
    isInReport,
  };

  const {
    setZooming,
    sizeRef,
    zooming: samplingZooming,
  } = usePanelInteractionContext();

  // The maybe variant: reports render line plots without a workspace drawer.
  // Only the stable openWorkspaceDrawer is passed, so opening or closing a
  // drawer does not change these props.
  const workspaceDrawer = useMaybeWorkspaceDrawerContext();
  // The page's entity and project, from the URL, for issue alert analytics.
  // The panel's own entity and project (configEntityName, configProjectName
  // below) can differ, for example in a report, and feed run links.
  const {entityName: routeEntityName, projectName: routeProjectName} =
    useParams<{
      entityName: string;
      projectName: string;
    }>();
  const {
    handleUserZoom,
    queryZoom,
    zooming: fullFidelityZooming,
  } = usePanelZoom2();
  const activeIssues = useActiveIssues();
  const annotationActive = useAnnotationActive();
  const handleIssueInteraction = useHandleIssueInteraction();
  const issueRampStatus = useIssueRampStatus();
  const setSelectedIssue = useSetSelectedIssue();
  const issueMarkers: GraphProps['issueMarkers'] = {
    activeIssueCount: (activeIssues ?? []).length,
    annotationActive,
    entityName: routeEntityName,
    handleIssueInteraction,
    hoveredIssueSource,
    issueRampStatus,
    openWorkspaceDrawer: workspaceDrawer?.openWorkspaceDrawer,
    projectName: routeProjectName,
    setSelectedIssue,
    xAxisMax: queryZoom.xAxisMax,
    xAxisMin: queryZoom.xAxisMin,
  };

  // The highlight changes on every crosshair move, so it goes down as a
  // stable source; components subscribe to only the axes they draw.
  const highlightSource = useHighlightSource();
  const setHighlight = useSetHighlight();
  const setHighlights = useSetHighlights();
  const highlight: GraphProps['highlight'] = {
    setHighlight,
    setHighlights,
    source: highlightSource,
  };

  const {
    excludeOutliers,
    isFullFidelity,
    isMultiMetric,
    showLegend,
    singlePointRenderMode,
    xAxis,
    xAxisIsDatetime,
    xLogScale,
    yLogScale,
  } = usePanelConfigContext();
  const {isSingleRun} = useSingleRunModeContext();
  const display: GraphProps['display'] = {
    excludeOutliers,
    showLegend,
    singlePointRenderMode,
  };

  // Both zoom systems are passed; Graph picks one by the isFullFidelity
  // setting.
  const {
    handleUserZoomChange,
    xDomainChart,
    yDomain: samplingYDomain,
  } = usePanelZoom();
  const {setTimeFactor} = usePanelTimeContext();
  const setSharedZoom = useSetSharedPanelZoom();
  const zoom: GraphProps['zoom'] = {
    fullFidelity: {
      handleUserZoom,
      queryZoom,
      zooming: fullFidelityZooming,
    },
    sampling: {
      handleUserZoomChange,
      xDomainChart,
      yDomain: samplingYDomain,
      zooming: samplingZooming,
    },
    setSharedZoom,
    setTimeFactor,
    setZooming,
  };

  // Settings for the renderers, crosshair, legend, and warning. Graph
  // provides them inside the unit through LinePlotSettingsProvider.
  const {
    colorRunNames,
    displayFullRunName,
    entityName: configEntityName,
    forceReactVis,
    groupingDepth,
    highlightedCompanionRunOnly,
    limit,
    plotType,
    pointVisualizationMethod,
    projectName: configProjectName,
    tooltipNumberOfRuns,
    xAxisFormat,
  } = usePanelConfigContext();
  const {isAnyRunsetGrouped} = usePanelGroupingSettings();
  const {isDarkMode} = useColorMode();
  const uplotEnabled = useRampFlagUPlotUser() === true;
  const uPlotMode = useUPlotMode();
  const colorPaletteOption = useWorkspaceSettingsColorPalette();
  const hasMediaPanel = useMaybeHasMediaPanel();
  const workspaceShowXRange = useMaybeLinePlotShowXRange();
  const sectionSettings = useSectionSettingsContextMaybe();
  const showXRange = getCascadingSetting(
    [workspaceShowXRange, sectionSettings?.linePlot?.showXRange],
    false
  );
  const setWorkspaceSliderSteps = useMaybeSetWorkspaceSliderSteps();
  function snapMediaPanelsToStep(step: number, sliderKey: string) {
    setWorkspaceSliderSteps?.(step, sliderKey, false, true);
  }
  const plotSettings: GraphProps['plotSettings'] = {
    colorPaletteOption,
    colorRunNames,
    displayFullRunName,
    entityName: configEntityName,
    forceReactVis,
    groupingDepth,
    hasMediaPanel,
    highlightedCompanionRunOnly,
    isAnyRunsetGrouped,
    isDarkMode,
    isFullFidelity,
    isMultiMetric,
    isSingleRun,
    limit,
    onSnapMediaPanelsToStep: snapMediaPanelsToStep,
    plotType,
    pointVisualizationMethod,
    projectName: configProjectName,
    showXRange,
    tooltipNumberOfRuns,
    uplotEnabled,
    uPlotMode,
    xAxis,
    xAxisFormat,
    xAxisIsDatetime,
    xLogScale,
    yLogScale,
  };

  return (
    <GraphWrapper
      {...props}
      cappedPanel={cappedPanel}
      display={display}
      highlight={highlight}
      issueMarkers={issueMarkers}
      panelId={panelId}
      plotSettings={plotSettings}
      sizeRef={sizeRef}
      zoom={zoom}
      zoomUsage={zoomUsage}
    />
  );
}
